import type { FinancialReconciliation } from '@/api/core/financial-reconciliation';
import { isApiError } from '@/api/http/errors';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button, DetailRow } from '@mgrvtechgroup/admin-ds';

const statusText = {
  MATCH: '저장된 금융 정보가 일치합니다',
  MISMATCH: '저장된 정보가 서로 일치하지 않습니다',
  PENDING: '전달 또는 원천 기록을 기다리고 있습니다',
  UNKNOWN: '현재 저장 근거만으로 판정할 수 없습니다',
  LIMITED: '관측 범위가 제한되었습니다',
} as const;

const fieldLabels: Record<string, string> = {
  order_aggregate_version: 'Order aggregate revision',
  order_row_version: 'Order row revision',
  order_id: '연결 주문 번호',
  current_attempt_id: '현재 결제 시도 ID',
  payment_approved: '주문 승인 사실 저장 여부',
  payment_id: 'Payment ID',
  attempt_id: 'Attempt ID',
  version: '저장 revision',
  status: '저장 상태',
  amount_minor: '저장 금액 (통화 최소 단위)',
  completed_minor: '기록된 완료액 (통화 최소 단위)',
  refunded_minor: '저장 환불 누계 (통화 최소 단위)',
  currency: '통화',
  event_id: '원천 event ID',
  source_version: '원천 revision',
  attempt_scope: 'Attempt 범위',
  event_type: '원천 종류',
  delivery_status: '주문 전달 결과',
  operation_id: 'Operation ID',
  payment_status: '결제 상태',
  sequence: '순번',
  classification: '실행 분류',
  cancel_kind: '취소 종류',
  created_at: '기록 시각',
  updated_at: '변경 시각',
  request_operation_id: '환불 의무 ID',
  capture_wait_recorded: '매입 대기 기록',
  fact_kind: '수용한 사실 종류',
};

const findingText: Record<string, string> = {
  APPROVAL_AMOUNT_MISMATCH: '승인 원천 금액이 주문 의무 금액과 다릅니다',
  APPROVAL_CURRENCY_MISMATCH: '승인 원천 통화가 주문 통화와 다릅니다',
  PAYMENT_AMOUNT_MISMATCH: '결제 금액이 주문 의무 금액과 다릅니다',
  PAYMENT_CURRENCY_MISMATCH: '결제 통화가 주문 통화와 다릅니다',
  PAYMENT_APPROVAL_STATE_MISMATCH: '결제 저장 상태와 승인 사실이 맞지 않습니다',
  SOURCE_BINDING_UNKNOWN: '원천과 현재 저장 binding을 확인할 수 없습니다',
  SOURCE_DELETED: '삭제된 원천이 포함되어 있습니다',
  SOURCE_UNSUPPORTED: '지원하지 않는 원천 종류가 포함되어 있습니다',
  PAYMENT_BINDING_UNKNOWN: '정확히 연결된 결제가 확인되지 않았습니다',
  PAYMENT_APPROVAL_OUTCOME_UNKNOWN: '결제 승인 결과가 미확정입니다',
  PAYMENT_OPERATION_OUTCOME_UNKNOWN: '환불 operation 결과가 미확정입니다',
  DELIVERY_RECEIPT_MISSING: '전달 결과에 대응하는 receipt가 없습니다',
  DELIVERY_RECEIPT_WITHOUT_OUTCOME: '전달 결과 없이 receipt만 기록되어 있습니다',
  DELIVERY_RECONCILIATION_RECORDED: '주문 전달 중 정합성 오류가 기록되었습니다',
  DUPLICATE_OPERATION_MISMATCH: '같은 환불 operation의 저장 사실이 서로 다릅니다',
  ORDER_APPROVAL_FACT_MISMATCH: '주문 승인 사실과 저장 receipt가 일치하지 않습니다',
  ORDER_APPROVAL_RECEIPT_MISSING: '주문 승인 사실에 대응하는 receipt가 없습니다',
  REFUNDED_TOTAL_MISMATCH: '저장 환불 누계와 확인된 operation 합계가 다릅니다',
  REFUND_REQUEST_TOTAL_MISMATCH: '환불 의무 완료액과 확인된 operation 합계가 다릅니다',
  SOURCE_PAYMENT_UNKNOWN: '원천에 연결된 결제를 확인할 수 없습니다',
  CURRENT_ATTEMPT_BINDING_MISSING: '현재 attempt의 결제 binding이 없습니다',
  RECEIPT_SOURCE_MISSING: '연결된 원천을 확인할 수 없는 receipt가 있습니다',
  PAYMENT_APPROVAL_SOURCE_MISSING: '승인 상태에 대응하는 원천 event가 없습니다',
  PAYMENT_APPROVAL_SOURCE_PENDING: '승인 원천 기록을 기다리고 있습니다',
  SOURCE_LIMIT_EXCEEDED: '조회 상한 초과로 일부 원천 기록이 제외되어 판정 범위가 제한되었습니다',
  PAYMENT_OPERATION_SOURCE_MISSING: '환불 operation에 대응하는 원천 event가 없습니다',
  PAYMENT_OPERATION_STATUS_UNKNOWN: '지원하지 않는 환불 operation 상태가 있습니다',
  RECEIPT_IDENTITY_UNKNOWN: 'receipt 식별 정보를 확인할 수 없습니다',
  REFUND_OPERATION_MISMATCH: '환불 실행과 원천 operation이 일치하지 않습니다',
  REFUND_REQUEST_REFERENCE_UNKNOWN: '환불 실행과 주문 환불 의무 연결을 확인할 수 없습니다',
  SOURCE_OPERATION_MISMATCH: '원천 event와 operation 연결이 일치하지 않습니다',
};

function Value({ value }: { value: string | number | boolean | null | undefined }) {
  if (value === null || value === undefined) return <>저장 값 없음</>;
  if (typeof value === 'boolean') return <>{value ? '예' : '아니요'}</>;
  return <>{String(value)}</>;
}

function ProjectionRows({
  value,
  orderCurrency,
}: {
  value: Record<string, string | number | boolean | null | undefined>;
  orderCurrency?: string;
}) {
  return (
    <dl className="grid min-w-0 gap-2 sm:grid-cols-2">
      {Object.entries(value).map(([key, item]) => (
        <DetailRow
          key={key}
          label={fieldLabels[key] ?? '기록 값'}
          value={
            key === 'amount_minor' || key === 'completed_minor' ? (
              item !== null && item !== undefined && !value.currency && orderCurrency ? (
                <>
                  {String(item)} (주문 통화 기준: {orderCurrency}; 통화 최소 단위)
                </>
              ) : (
                <Value value={item} />
              )
            ) : (
              <Value
                value={
                  (key === 'created_at' || key === 'updated_at') && typeof item === 'string'
                    ? formatDateTimeWithOffset(item)
                    : item
                }
              />
            )
          }
        />
      ))}
    </dl>
  );
}

function Evidence({
  title,
  rows,
  orderCurrency,
}: {
  title: string;
  rows: Record<string, string | number | boolean | null | undefined>[];
  orderCurrency?: string;
}) {
  return (
    <details className="min-w-0 rounded-md border p-3">
      <summary className="cursor-pointer font-medium">
        {title} · {rows.length}건
      </summary>
      {rows.length === 0 ? (
        <p className="body-sm pt-3">확인된 기록이 없습니다.</p>
      ) : (
        <ol className="grid min-w-0 gap-3 pt-3">
          {rows.map((row, index) => (
            <li className="min-w-0 border-t pt-3" key={`${title}-${String(index)}`}>
              <ProjectionRows value={row} orderCurrency={orderCurrency} />
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}

export function FinancialReconciliationSection({
  result,
  fetching,
  error,
  canRead,
  requested,
  onRead,
}: {
  result: FinancialReconciliation | undefined;
  fetching: boolean;
  error: unknown;
  canRead: boolean;
  requested: boolean;
  onRead: () => void;
}) {
  const visible = requested && !fetching && !error ? result : undefined;
  const code = isApiError(error) ? error.status : undefined;
  return (
    <section aria-label="금융 정합성 조회" className="min-w-0 space-y-3 border-t pt-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="title-sm">금융 정합성 조회</h3>
          <p className="body-sm">주문과 결제에 저장된 금융 정보를 비교합니다.</p>
        </div>
        <Button variant="outline" disabled={!canRead || fetching} onClick={onRead}>
          {fetching ? '정합성 조회 중' : requested ? '금융 정합성 다시 조회' : '금융 정합성 조회'}
        </Button>
      </div>
      {canRead ? (
        fetching ? (
          <p role="status" className="body-sm">
            현재 연결된 주문의 금융 정합성을 조회하고 있습니다.
          </p>
        ) : error ? (
          <div role="alert" className="space-y-1">
            <p className="body-sm">
              {code === 403
                ? '현재 주문과 결제 권한 확인이 거부되었습니다. 어느 권한이 거부됐는지 알 수 없어 정합성 결과만 숨겼습니다.'
                : code === 404
                  ? '선택한 연결 주문의 금융 정합성 자료를 찾을 수 없습니다.'
                  : '금융 정합성을 조회하지 못했습니다. 다시 조회해 주세요.'}
            </p>
          </div>
        ) : visible ? (
          <div className="min-w-0 space-y-3">
            <div role="status" className="rounded-md border p-3">
              <p className="font-medium">{statusText[visible.status]}</p>
              <p className="body-sm mt-1 break-words">
                관측 시각 {formatDateTimeWithOffset(visible.observed_at)} · 저장 금액{' '}
                {visible.order.amount_minor} {visible.order.currency} (통화 최소 단위)
              </p>
              <p className="body-sm mt-1">
                환불 누계 {visible.order.refunded_minor} {visible.order.currency} (통화 최소 단위)
              </p>
              {visible.status === 'MATCH' && (
                <p className="body-sm mt-1">
                  저장된 정보가 일치해도 외부 결제 성공이나 환불 완료를 뜻하지는 않습니다.
                </p>
              )}
            </div>
            <section aria-label="판정 근거">
              <h4 className="font-medium">판정 근거</h4>
              {visible.findings.length === 0 ? (
                <p className="body-sm">표시할 판정 항목이 없습니다.</p>
              ) : (
                <ul className="list-disc space-y-1 pl-5">
                  {visible.findings.map((finding, index) => (
                    <li className="body-sm" key={`${finding}-${String(index)}`}>
                      {findingText[finding] ?? '추가 확인이 필요한 항목이 있습니다.'}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section aria-label="판정 범위">
              <h4 className="font-medium">판정 범위</h4>
              <ul className="list-disc space-y-1 pl-5">
                {visible.judgment_scope.map((scope, index) => (
                  <li className="body-sm" key={`${scope}-${String(index)}`}>
                    {scope}
                  </li>
                ))}
              </ul>
            </section>
            <div className="grid min-w-0 gap-2">
              <Evidence
                title="주문과 결제 revisions"
                rows={[
                  {
                    order_aggregate_version: visible.revisions.order_aggregate_version,
                    order_row_version: visible.revisions.order_row_version,
                  },
                  ...visible.revisions.payments.map((row) => ({
                    payment_id: row.payment_id,
                    attempt_id: row.attempt_id,
                    version: row.version,
                  })),
                  ...visible.revisions.payment_sources.map((row) => ({
                    event_id: row.event_id,
                    source_version: row.source_version,
                  })),
                ]}
              />
              <Evidence title="연결된 주문 식별" rows={[{ order_id: visible.order_id }]} />
              <Evidence title="주문 저장 사실" rows={[visible.order]} />
              <Evidence title="연결된 결제 저장 사실" rows={visible.payments} />
              <Evidence title="금융 원천과 전달 결과" rows={visible.sources} />
              <Evidence title="결제 환불 실행" rows={visible.refund_operations} />
              <Evidence
                title="주문 환불 의무"
                rows={visible.refund_requests}
                orderCurrency={visible.order.currency}
              />
              <Evidence title="주문이 수용한 receipt" rows={visible.receipts} />
            </div>
          </div>
        ) : requested ? (
          <p role="status" className="body-sm">
            아직 표시할 정합성 결과가 없습니다.
          </p>
        ) : null
      ) : (
        <p role="status" className="body-sm">
          주문과 결제 조회 권한이 모두 있어야 정합성을 조회할 수 있습니다.
        </p>
      )}
    </section>
  );
}
